import React from 'react';
import { format, isSameDay } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { RECOMMENDATION_COLORS, formatDate, toDate } from './reportUtils';

const RECOMMENDATION_DOTS = {
  'Strong Hire': 'bg-emerald-500',
  'Hire': 'bg-green-500',
  'No Hire': 'bg-red-500',
  'Hold': 'bg-amber-500'
};

const hasTime = (value) => /T\d{2}:\d{2}/.test(value || '');

// How a round went: the recommendation once completed, otherwise its status.
// A scheduled round still ahead is upcoming.
const roundOutcome = ({ status, recommendation }, date) => {
  if (status === 'Completed') {
    return recommendation
      ? {
        text: `Completed · ${recommendation}`,
        className: RECOMMENDATION_COLORS[recommendation] || 'text-slate-700',
        dot: RECOMMENDATION_DOTS[recommendation] || 'bg-green-500'
      }
      : { text: 'Completed', className: 'text-green-700', dot: 'bg-green-500' };
  }
  if (status === 'Scheduled') {
    return date && date > new Date()
      ? { text: 'Upcoming', className: 'text-blue-700', dot: 'bg-blue-500' }
      : { text: 'Scheduled', className: 'text-blue-700', dot: 'bg-blue-300' };
  }
  if (status === 'In Progress') return { text: 'In progress', className: 'text-blue-700', dot: 'bg-blue-500' };
  if (status === 'No Show') return { text: 'No show', className: 'text-amber-700', dot: 'bg-amber-500' };
  return { text: status || 'No status', className: 'text-slate-500', dot: 'bg-slate-300' };
};

const byUser = (user) => (user ? `by ${user}` : '');

// Every date the candidate's record holds, oldest first: when they were added,
// each move to another stage, and each interview round - with the days the
// round was added and its feedback given.
const keyDates = (candidate, interviews) => {
  const history = candidate.status_history || [];
  const startStage = history.find((entry) => entry.stage)?.stage;
  const events = [{
    key: 'added',
    at: candidate.created_at,
    title: 'Added to portal',
    notes: [[startStage && `in ${startStage}`, byUser(candidate.created_by)].filter(Boolean).join(' · ')],
    dot: 'bg-purple-600'
  }];

  // The first row is the stage they were added at. Status changes write a
  // row too, repeating the stage, so only a different stage is a move.
  let stage = null;
  history.forEach((entry, n) => {
    if (!entry.stage) return;
    if (stage && entry.stage !== stage) {
      events.push({
        key: `move-${n}`,
        at: entry.timestamp,
        title: `Moved to ${entry.stage}`,
        notes: [byUser(entry.changed_by)],
        dot: 'bg-purple-300'
      });
    }
    stage = entry.stage;
  });

  interviews.forEach((interview) => {
    const date = toDate(interview.scheduled_at);
    const added = toDate(interview.created_at);
    const outcome = roundOutcome(interview, date);
    events.push({
      key: `round-${interview.id}`,
      at: interview.scheduled_at,
      title: [interview.round_name, interview.round_number && `Round ${interview.round_number}`].filter(Boolean).join(' · '),
      outcome,
      interviewer: interview.interviewer_name,
      notes: [[
        // A round added on its own day (general feedback) needs no second date.
        added && !(date && isSameDay(added, date)) && `Added ${format(added, 'dd MMM yyyy')} ${byUser(interview.created_by)}`,
        interview.feedback_at && `Feedback ${formatDate(interview.feedback_at)} ${byUser(interview.feedback_by)}`
      ].filter(Boolean).map((note) => note.trim()).join(' · ')],
      dot: outcome.dot
    });
  });

  // Oldest first; a round without a date goes last.
  return events
    .map((event, order) => ({ ...event, date: toDate(event.at), order }))
    .sort((a, b) => (
      (a.date ? 0 : 1) - (b.date ? 0 : 1) || (a.date && b.date ? a.date - b.date : 0) || a.order - b.order
    ));
};

const EventDate = ({ value, date }) => (
  date ? (
    <time dateTime={date.toISOString()} className="shrink-0 text-right text-sm tabular-nums text-slate-700">
      {format(date, 'dd MMM yyyy')}
      {hasTime(value) && <span className="block text-xs text-slate-500">{format(date, 'h:mm a')}</span>}
    </time>
  ) : (
    <span className="shrink-0 text-sm text-slate-400">No date</span>
  )
);

const CandidateKeyDates = ({ candidate, interviews }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm">Key Dates</CardTitle>
    </CardHeader>
    <CardContent>
      <ol
        className="relative flex flex-col gap-4 before:absolute before:bottom-2 before:left-[4.5px] before:top-2 before:w-px before:bg-slate-200"
        data-testid="candidate-key-dates"
      >
        {keyDates(candidate, interviews).map((event) => (
          <li key={event.key} className="relative flex items-start justify-between gap-3 pl-5">
            <span className={`absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${event.dot}`} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">{event.title}</p>
              {event.outcome && (
                <p className="text-xs">
                  <span className={event.outcome.className}>{event.outcome.text}</span>
                  {event.interviewer && <span className="text-slate-500"> · with {event.interviewer}</span>}
                </p>
              )}
              {event.notes.filter(Boolean).map((note) => (
                <p key={note} className="text-xs text-slate-500">{note}</p>
              ))}
            </div>
            <EventDate value={event.at} date={event.date} />
          </li>
        ))}
      </ol>
    </CardContent>
  </Card>
);

export default CandidateKeyDates;
